import { supabase, isDemoMode } from './supabase';
import {
  getStudentDailyLivingLog, getStudentCompletions, getAllStudentsDailyLivingCompletions,
} from './dailyLivingService';
import { getDemoAchievements, addDemoAchievement } from './demoData';

// ─── BADGE DEFINITIONS ──────────────────────────────────────
// stats.scenariosCompleted is tracked SEPARATELY from totalCorrect/word-based
// stats (DLINT-02) — it is never blended into totalCorrect or firstTryCorrect.
export const BADGE_DEFINITIONS = [
  { key: 'first_word',      label: 'First Word',      icon: 'fas fa-star',         description: 'Answer your first question correctly', check: (stats) => stats.totalCorrect >= 1 },
  { key: 'streak_3',        label: '3-Day Streak',    icon: 'fas fa-fire',         description: 'Be active for 3 days in a row', check: (_s, streak) => streak >= 3 },
  { key: 'brain_buster',    label: 'Brain Buster',    icon: 'fas fa-brain',        description: 'Get 10 total questions correct', check: (stats) => stats.totalCorrect >= 10 },
  { key: 'quick_solver',    label: 'Quick Solver',    icon: 'fas fa-bolt',         description: 'Answer 5 questions correctly on the first try', check: (stats) => stats.firstTryCorrect >= 5 },
  { key: 'word_master',     label: 'Word Master',     icon: 'fas fa-crown',        description: 'Get 25 total questions correct', check: (stats) => stats.totalCorrect >= 25 },
  { key: 'puzzle_pro',      label: 'Puzzle Pro',      icon: 'fas fa-puzzle-piece', description: 'Get 10 Scramble Words correct', check: (stats) => stats.scrambleCorrect >= 10 },
  { key: 'category_king',   label: 'Category King',   icon: 'fas fa-tags',         description: 'Get 10 Category Words correct', check: (stats) => stats.categoryCorrect >= 10 },
  { key: 'perfect_score',   label: 'Perfect Score',   icon: 'fas fa-award',        description: 'Answer 15 questions correctly on the first try', check: (stats) => stats.firstTryCorrect >= 15 },
  { key: 'math_starter',    label: 'Math Starter',    icon: 'fas fa-calculator',   description: 'Get 5 Math Concepts correct', check: (stats) => stats.mathConceptsCorrect >= 5 },
  { key: 'math_wizard',     label: 'Math Wizard',     icon: 'fas fa-hat-wizard',   description: 'Get 15 Math Concepts correct', check: (stats) => stats.mathConceptsCorrect >= 15 },
  { key: 'first_life_skill', label: 'Life Skills Star', icon: 'fas fa-hands-wash', description: 'Complete your first Daily Living scenario', check: (stats) => stats.scenariosCompleted >= 1 },
];

// ─── DEMO DATA ───────────────────────────────────────────────

const DEMO_PROGRESS = {
  sight:         { correct: 4, total: 5 },
  scramble:      { correct: 3, total: 5 },
  category:      { correct: 2, total: 5 },
  math_concepts: { correct: 3, total: 5 },
};

function buildDemoWeekData(weekStart) {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    days.push({ date: d.toISOString().slice(0, 10), hasActivity: i < 5, perfect: i < 4 });
  }
  return days;
}

// ─── LOG A SINGLE WORD ATTEMPT ───────────────────────────────
// voiceScore: 0-100 float, optional. Only set for sight-word activities.
// Null is stored for scramble/category (backwards compatible — default param).

export async function logActivity(studentId, activityType, wordId, correct, attemptsUsed, voiceScore = null) {
  if (isDemoMode) return { error: null };
  return supabase.from('student_activity_log').insert({
    student_id:    studentId,
    activity_type: activityType,
    word_id:       wordId,
    correct,
    attempts_used: attemptsUsed,
    voice_score:   voiceScore,
  });
}

// ─── PROGRESS ────────────────────────────────────────────────

export async function getStudentProgress(studentId) {
  if (isDemoMode) {
    const { data: completions } = await getStudentCompletions(studentId);
    return { data: { ...DEMO_PROGRESS, scenariosCompleted: (completions || []).length }, error: null };
  }

  const [logRes, completionsRes] = await Promise.all([
    supabase.from('student_activity_log').select('activity_type, correct').eq('student_id', studentId),
    getStudentCompletions(studentId),
  ]);

  if (logRes.error) return { data: null, error: logRes.error };

  const result = {
    sight:         { correct: 0, total: 0 },
    scramble:      { correct: 0, total: 0 },
    category:      { correct: 0, total: 0 },
    math_concepts: { correct: 0, total: 0 },
  };
  for (const row of logRes.data) {
    if (!result[row.activity_type]) continue;
    result[row.activity_type].total += 1;
    if (row.correct) result[row.activity_type].correct += 1;
  }

  // Daily Living "scenarios completed" is tracked separately (DLINT-02) —
  // never blended into the word-based correct/total counters above.
  result.scenariosCompleted = (completionsRes.data || []).length;

  return { data: result, error: null };
}

// ─── WEEKLY ACTIVITY ─────────────────────────────────────────

export async function getWeeklyActivity(studentId, weekStart) {
  if (isDemoMode) return { data: buildDemoWeekData(weekStart), error: null };

  const start = new Date(weekStart);
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 7);

  const [logRes, dlLogRes] = await Promise.all([
    supabase
      .from('student_activity_log')
      .select('correct, completed_at')
      .eq('student_id', studentId)
      .gte('completed_at', start.toISOString())
      .lt('completed_at', end.toISOString()),
    getStudentDailyLivingLog(studentId),
  ]);

  if (logRes.error) return { data: null, error: logRes.error };

  // Merge word-based attempts with Daily Living step attempts into the same
  // day/perfect calculation — a step attempt counts the same as a word
  // attempt for "hasActivity"/"perfect" purposes (DLINT-05: explicit rule).
  const dlRowsInRange = (dlLogRes.data || []).filter(
    (r) => r.completed_at >= start.toISOString() && r.completed_at < end.toISOString()
  );
  const combined = [...logRes.data, ...dlRowsInRange];

  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const dateStr = d.toISOString().slice(0, 10);
    const dayRows = combined.filter((r) => r.completed_at.slice(0, 10) === dateStr);
    const hasActivity = dayRows.length > 0;
    const perfect = hasActivity && dayRows.every((r) => r.correct);
    days.push({ date: dateStr, hasActivity, perfect });
  }
  return { data: days, error: null };
}

// ─── ACHIEVEMENTS ─────────────────────────────────────────────

export async function getStudentAchievements(studentId) {
  if (isDemoMode) return { data: getDemoAchievements(studentId), error: null };
  const { data, error } = await supabase
    .from('student_achievements').select('badge_key').eq('student_id', studentId);
  if (error) return { data: null, error };
  return { data: data.map((r) => r.badge_key), error: null };
}

export async function checkAndAwardAchievements(studentId) {
  if (isDemoMode) {
    // Demo mode: evaluate against the fixed DEMO_PROGRESS word stats plus the
    // live (session-mutable) Daily Living completions, so first_life_skill
    // (and any other badge) can actually unlock end-to-end without Supabase.
    const { data: completions } = await getStudentCompletions(studentId);
    const demoStats = {
      totalCorrect:        DEMO_PROGRESS.sight.correct + DEMO_PROGRESS.scramble.correct + DEMO_PROGRESS.category.correct + DEMO_PROGRESS.math_concepts.correct,
      firstTryCorrect:     0,
      scrambleCorrect:     DEMO_PROGRESS.scramble.correct,
      categoryCorrect:     DEMO_PROGRESS.category.correct,
      mathConceptsCorrect: DEMO_PROGRESS.math_concepts.correct,
      scenariosCompleted:  (completions || []).length,
    };
    const earnedKeys = new Set(getDemoAchievements(studentId));
    BADGE_DEFINITIONS
      .filter((b) => !earnedKeys.has(b.key) && b.check(demoStats, 3))
      .forEach((b) => addDemoAchievement(studentId, b.key));
    return;
  }

  const [logRes, completionsRes] = await Promise.all([
    supabase
      .from('student_activity_log')
      .select('correct, attempts_used, activity_type, completed_at')
      .eq('student_id', studentId),
    getStudentCompletions(studentId),
  ]);

  const log = logRes.data;
  if (!log) return;

  const completions = completionsRes.data || [];

  const stats = {
    totalCorrect:        log.filter((r) => r.correct).length,
    firstTryCorrect:     log.filter((r) => r.correct && r.attempts_used === 1).length,
    scrambleCorrect:     log.filter((r) => r.correct && r.activity_type === 'scramble').length,
    categoryCorrect:     log.filter((r) => r.correct && r.activity_type === 'category').length,
    mathConceptsCorrect: log.filter((r) => r.correct && r.activity_type === 'math_concepts').length,
    // Separate stat (DLINT-02) — deliberately not folded into totalCorrect.
    scenariosCompleted:  completions.length,
  };

  // Daily Living completions also count toward the day-streak, alongside word activity.
  const activeDays = [...new Set([
    ...log.map((r) => r.completed_at.slice(0, 10)),
    ...completions.map((r) => r.completed_at.slice(0, 10)),
  ])].sort();
  let streak = 0;
  const today = new Date().toISOString().slice(0, 10);
  let check = today;
  for (let i = activeDays.length - 1; i >= 0; i--) {
    if (activeDays[i] === check) {
      streak++;
      const d = new Date(check);
      d.setDate(d.getDate() - 1);
      check = d.toISOString().slice(0, 10);
    } else {
      break;
    }
  }

  const { data: earned } = await supabase
    .from('student_achievements').select('badge_key').eq('student_id', studentId);
  const earnedKeys = new Set((earned || []).map((r) => r.badge_key));

  const toInsert = BADGE_DEFINITIONS
    .filter((b) => !earnedKeys.has(b.key) && b.check(stats, streak))
    .map((b) => ({ student_id: studentId, badge_key: b.key }));

  if (toInsert.length > 0) {
    await supabase.from('student_achievements').insert(toInsert);
  }
}

// ─── ADMIN: per-student summary ───────────────────────────────

export async function getAllStudentsActivitySummary() {
  if (isDemoMode) {
    const { data: completions } = await getStudentCompletions('demo-student-001');
    return {
      data: [
        {
          student_id:         'demo-student-001',
          name:               'Alex Student',
          total:              14,
          correct:            9,
          avgVoiceScore:      72.5,
          scenariosCompleted: (completions || []).length,
          lastActive:         new Date().toISOString().slice(0, 10),
        },
        {
          student_id:         'demo-student-002',
          name:               'Jamie Learner',
          total:              0,
          correct:            0,
          avgVoiceScore:      null,
          scenariosCompleted: 0,
          lastActive:         null,
        },
      ],
      error: null,
    };
  }

  const [profilesRes, logRes, dlCompletionsRes] = await Promise.all([
    supabase.from('profiles').select('id, first_name, last_name').eq('role', 'student'),
    supabase.from('student_activity_log').select('student_id, correct, voice_score, completed_at'),
    getAllStudentsDailyLivingCompletions(),
  ]);

  if (profilesRes.error) return { data: null, error: profilesRes.error };
  if (logRes.error)      return { data: null, error: logRes.error };

  const dlCompletions = dlCompletionsRes.data || [];

  const data = profilesRes.data.map((p) => {
    const rows      = logRes.data.filter((r) => r.student_id === p.id);
    const dlRows    = dlCompletions.filter((r) => r.student_id === p.id);
    const dates     = [...rows.map((r) => r.completed_at), ...dlRows.map((r) => r.completed_at)].sort();
    const voiceRows = rows.filter((r) => r.voice_score != null);
    const avgVoiceScore = voiceRows.length > 0
      ? Math.round((voiceRows.reduce((s, r) => s + r.voice_score, 0) / voiceRows.length) * 10) / 10
      : null;

    return {
      student_id:         p.id,
      name:               `${p.first_name} ${p.last_name}`,
      total:              rows.length,
      correct:            rows.filter((r) => r.correct).length,
      avgVoiceScore,
      // Separate stat (DLINT-02): count of completed Daily Living scenarios,
      // NOT blended into total/correct word-based accuracy above.
      scenariosCompleted: dlRows.length,
      lastActive:         dates.length > 0 ? dates[dates.length - 1].slice(0, 10) : null,
    };
  });

  return { data, error: null };
}

// ─── EMAIL ANALYTICS REPORT ───────────────────────────────────

export async function sendAnalyticsReport(emails, csvContent) {
  if (isDemoMode) {
    await new Promise((r) => setTimeout(r, 500));
    return { error: null };
  }
  return supabase.functions.invoke('send-report', {
    body: { emails, csvContent },
  });
}
